// src/pages/PrizeStructure/PrizeStructureTable.tsx
import React, { useMemo } from 'react';
import { Button, Input } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { PrizeTierConfig, MatchingRule, MatchingPosition } from './types';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import DataTableShell from 'Components/Common/DataTableShell';
import NumberInput from 'Components/Common/NumberInput';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('prize_structure');

interface Props {
  tiers: PrizeTierConfig[];
  loading: boolean;
  deletingCode: string | null;
  onFieldChange: (code: string, field: keyof PrizeTierConfig, value: any) => void;
  onDelete: (tier: PrizeTierConfig) => void;
}

const MATCHING_RULE_OPTIONS: { value: MatchingRule; label: string }[] = [
  { value: 'exact', label: st('khop_dung_exact_label') },
  { value: 'last_n', label: st('khop_n_so_cuoi_last_n_label') },
  { value: 'first_n_wrong', label: st('sai_so_dau_khop_n_so_label') },
  { value: 'any_n_wrong', label: st('sai_dung_n_so_bat_ky_label') },
];

const MATCHING_POSITION_OPTIONS: { value: MatchingPosition; label: string }[] = [
  { value: 'all', label: 'all' },
  { value: 'first', label: 'first' },
  { value: 'last', label: 'last' },
];

const formatCurrency = (value: number) => (value || 0).toLocaleString('vi-VN') + st('d_text');

const PrizeStructureTable: React.FC<Props> = ({ tiers, loading, deletingCode, onFieldChange, onDelete }) => {
  const location = useLocation();
  // Preview realtime tổng chi trả — tính phía client, không cần gọi API (ui_co_cau_giai_thuong.md mục 5)
  const totalPayout = useMemo(
    () => tiers.reduce((sum, t) => sum + (t.quantity || 0) * (t.valuePerTicket || 0), 0),
    [tiers]
  );

  return (
    <DataTableShell
      headerRow={
        <tr>
          <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
          {/* "Giải" cần width cố định — nếu để trống, table-layout:auto sẽ đổ hết phần rộng còn thừa của
              container vào cột này (mọi cột khác đều có width cố định), làm tên giải bị giãn quá to. */}
          <th style={{ width: 140 }}>{st('giai_column')}</th>
          <th style={{ width: 100 }}>{st('so_luong_label')}</th>
          <th style={{ width: 180 }}>{st('gia_tri_ve_label')}</th>
          <th style={{ width: 160 }}>{st('tong_gia_tri_column')}</th>
          {/* Không set width cố định — chủ động nhận phần không gian còn thừa vì chứa nhiều input nhất
              (số chữ số + quy tắc khớp + tham số quy tắc). */}
          <th style={{ minWidth: 280 }}>{st('dieu_kien_trung_column')}</th>
          <th style={{ width: 60 }}></th>
        </tr>
      }
      footerRow={
        !loading && tiers.length > 0 ? (
          <tr>
            <td colSpan={4} className="text-end fw-semibold">
              {st('tong_chi_tra_du_kien_text')}
            </td>
            <td className="fw-semibold">{formatCurrency(totalPayout)}</td>
            <td colSpan={2}></td>
          </tr>
        ) : undefined
      }
    >
          {loading ? (
            Array.from({ length: 10 }).map((_, i) => (
              <tr key={i}>
                {Array.from({ length: 7 }).map((__, j) => (
                  <td key={j}>
                    <div className="placeholder-glow">
                      <span className="placeholder col-8"></span>
                    </div>
                  </td>
                ))}
              </tr>
            ))
          ) : (
            tiers.map((tier, rowIndex) => (
              <tr key={tier.code}>
                <td className="text-center">{rowIndex + 1}</td>
                <td className="fw-semibold">{tier.displayName}</td>
                <td>
                  <NumberInput
                    value={tier.quantity}
                    data-action-code="input-quantity"
                    data-action-name="Số lượng giải"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'input-quantity')}
                    onChange={(val) => onFieldChange(tier.code, 'quantity', val)}
                  />
                </td>
                <td>
                  <NumberInput
                    value={tier.valuePerTicket}
                    data-action-code="input-value-per-ticket"
                    data-action-name="Giá trị mỗi vé"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'input-value-per-ticket')}
                    onChange={(val) => onFieldChange(tier.code, 'valuePerTicket', val)}
                  />
                </td>
                <td>{formatCurrency((tier.quantity || 0) * (tier.valuePerTicket || 0))}</td>
                <td>
                  <div className="d-flex flex-wrap align-items-center gap-1">
                    <NumberInput
                      style={{ width: 64 }}
                      value={tier.digitCondition}
                      data-action-code="input-digit-condition"
                      data-action-name="Số chữ số điều kiện"
                      disabled={!PageActionPermission.canEnable(location.pathname, 'input-digit-condition')}
                      onChange={(val) => onFieldChange(tier.code, 'digitCondition', val)}
                    />
                    <span className="text-muted small">{st('chu_so_text')}</span>
                    <Input
                      type="select"
                      bsSize="sm"
                      style={{ width: 190 }}
                      value={tier.matchingRule}
                      data-action-code="input-matching-rule"
                      data-action-name="Quy tắc khớp"
                      disabled={!PageActionPermission.canEnable(location.pathname, 'input-matching-rule')}
                      onChange={(e) => onFieldChange(tier.code, 'matchingRule', e.target.value as MatchingRule)}
                    >
                      {MATCHING_RULE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                    {tier.matchingRule !== 'exact' && (
                      <>
                        <Input
                          type="select"
                          bsSize="sm"
                          style={{ width: 90 }}
                          value={tier.matchingPosition ?? 'all'}
                          data-action-code="input-matching-position"
                          data-action-name="Vị trí khớp"
                          disabled={!PageActionPermission.canEnable(location.pathname, 'input-matching-position')}
                          onChange={(e) => onFieldChange(tier.code, 'matchingPosition', e.target.value as MatchingPosition)}
                        >
                          {MATCHING_POSITION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                        <NumberInput
                          bsSize="sm"
                          style={{ width: 64 }}
                          placeholder={st('n_placeholder')}
                          value={tier.matchingDigits}
                          data-action-code="input-matching-digits"
                          data-action-name="Số chữ số khớp (N)"
                          disabled={!PageActionPermission.canEnable(location.pathname, 'input-matching-digits')}
                          onChange={(val) => onFieldChange(tier.code, 'matchingDigits', val)}
                        />
                      </>
                    )}
                  </div>
                </td>
                <td className="text-center">
                  {PageActionPermission.canDisplay(location.pathname, 'button-delete') && (
                  <Button
                    color="link"
                    className="text-danger p-0"
                    title={st('xoa_display_name_text', { display_name: tier.displayName })}
                    data-action-code="button-delete"
                    data-action-name="Xoá hạng giải"
                    disabled={deletingCode === tier.code}
                    onClick={() => onDelete(tier)}
                  >
                    <i className="ri-delete-bin-line"></i>
                  </Button>
                  )}
                </td>
              </tr>
            ))
          )}
    </DataTableShell>
  );
};

export default PrizeStructureTable;
